import { toast } from 'sonner';
import i18next from 'i18next';
import { Logger } from '@/lib/app-log.js';
import { getHttpErrorStatus } from '@/lib/get-http-error-status.js';
import { useMatrixConfigurationStore } from '../../store/matrix-configuration.store.js';

/**
 * SDK → Store bridge for tenant matrix configuration (risk matrix + CIA matrix)
 */
export class MatrixConfigurationIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchMatrixConfiguration() {
        const store = useMatrixConfigurationStore.getState();
        try {
            store.setLoading(true);
            store.setError(null);

            Logger.debug('[MatrixConfigurationIntegration] Fetching matrix configuration');
            const response = await this.service.getMatrixConfiguration();

            store.setMatrixConfiguration(response.data);
            store.setLastUpdated(new Date().toISOString());

            return { success: true, data: response.data };
        } catch (error) {
            const status = getHttpErrorStatus(error);
            if (status === 401) {
                store.setError(null);
                return { success: false, error: 'unauthorized' };
            }
            Logger.error('[MatrixConfigurationIntegration] getMatrixConfiguration error', error);
            store.setError(error.message || 'Failed to fetch matrix configuration');
            throw error;
        } finally {
            store.setLoading(false);
        }
    }

    async updateMatrixConfiguration(data) {
        const store = useMatrixConfigurationStore.getState();
        try {
            store.setLoading(true);
            store.setError(null);

            Logger.debug('[MatrixConfigurationIntegration] Updating matrix configuration', { data });
            const response = await this.service.updateMatrixConfiguration(data);

            store.setMatrixConfiguration(response.data);
            store.setLastUpdated(new Date().toISOString());
            store.setIsEditing(false);

            toast.success(
                i18next.t('matrixConfiguration:messages.saveSuccess', {
                    defaultValue: 'Matrix configuration saved successfully',
                }),
            );

            return { success: true, data: response.data };
        } catch (error) {
            Logger.error('[MatrixConfigurationIntegration] updateMatrixConfiguration error', error);
            const message =
                error.message || i18next.t('matrixConfiguration:matrix.error.saveFailed', {
                    defaultValue: 'Save failed',
                });
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    notifyMatrixValidationError(message) {
        toast.error(
            message ||
                i18next.t('matrixConfiguration:matrix.error.validationFailed', {
                    defaultValue: 'Validation error',
                }),
        );
    }

    async updateRiskRows(rows) {
        const store = useMatrixConfigurationStore.getState();
        const current = store.matrixConfiguration;
        if (!current?.riskMatrix) {
            await this.fetchMatrixConfiguration();
        }
        const mc = useMatrixConfigurationStore.getState().matrixConfiguration;
        return this.updateMatrixConfiguration({
            riskMatrix: { ...mc.riskMatrix, rows },
        });
    }

    async updateRiskColumns(columns) {
        const store = useMatrixConfigurationStore.getState();
        const current = store.matrixConfiguration;
        if (!current?.riskMatrix) {
            await this.fetchMatrixConfiguration();
        }
        const mc = useMatrixConfigurationStore.getState().matrixConfiguration;
        return this.updateMatrixConfiguration({
            riskMatrix: { ...mc.riskMatrix, columns },
        });
    }

    async updateCiaConfidentialityLevels(confidentialityLevels) {
        await this._ensureCia();
        const mc = useMatrixConfigurationStore.getState().matrixConfiguration;
        return this.updateMatrixConfiguration({
            ciaMatrix: { ...mc.ciaMatrix, confidentialityLevels },
        });
    }

    async updateCiaIntegrityLevels(integrityLevels) {
        await this._ensureCia();
        const mc = useMatrixConfigurationStore.getState().matrixConfiguration;
        return this.updateMatrixConfiguration({
            ciaMatrix: { ...mc.ciaMatrix, integrityLevels },
        });
    }

    async updateCiaAvailabilityLevels(availabilityLevels) {
        await this._ensureCia();
        const mc = useMatrixConfigurationStore.getState().matrixConfiguration;
        return this.updateMatrixConfiguration({
            ciaMatrix: { ...mc.ciaMatrix, availabilityLevels },
        });
    }

    async updateCiaMatrix(ciaMatrix) {
        return this.updateMatrixConfiguration({ ciaMatrix });
    }

    async _ensureCia() {
        const store = useMatrixConfigurationStore.getState();
        if (!store.matrixConfiguration?.ciaMatrix) {
            await this.fetchMatrixConfiguration();
        }
    }
}
